import React, { useState, useEffect } from 'react';
import '../styles/RangeSelector.css';
import QuranAPI, { RANGE_LIMITS } from '../services/quranAPI';

// Libellés par type de plage
const RANGE_LABELS = {
  page: { title: 'Sélection par pages', singular: 'page', plural: 'pages' },
  juz: { title: 'Sélection par juz', singular: 'juz', plural: 'juz' },
  surah: { title: 'Sélection par sourates', singular: 'sourate', plural: 'sourates' }
};

// Raccourcis de plage proposés selon le type sélectionné
const RANGE_PRESETS = {
  page: [
    { label: '5 pages', size: 5 },
    { label: '10 pages', size: 10 },
    { label: '20 pages', size: 20 },
    { label: 'Tout', size: RANGE_LIMITS.page }
  ],
  juz: [
    { label: '1 juz', size: 1 },
    { label: '5 juz', size: 5 },
    { label: '10 juz', size: 10 },
    { label: 'Tout', size: RANGE_LIMITS.juz }
  ],
  surah: [
    { label: '5 sourates', size: 5 },
    { label: '10 sourates', size: 10 },
    { label: 'Juz Amma', start: 78, end: 114 },
    { label: 'Tout', size: RANGE_LIMITS.surah }
  ]
};

// Icônes pour le sélecteur
const PageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const ArrowIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

function RangeSelector({
  rangeType,
  startRange,
  endRange,
  onStartRangeChange,
  onEndRangeChange
}) {
  const [allSurahNames, setAllSurahNames] = useState({}); // Pour stocker tous les noms de 1 à 114
  const [loadingSurahNames, setLoadingSurahNames] = useState(false);

  // Charger tous les noms des sourates une fois
  useEffect(() => {
    async function loadAllSurahNames() {
      setLoadingSurahNames(true);
      try {
        const names = {};
        // Essayer de récupérer la liste complète des sourates via une nouvelle fonction API optimisée
        const surahListResponse = await QuranAPI.getSurahList();
        if (surahListResponse && surahListResponse.data) {
          surahListResponse.data.forEach(surah => {
            names[surah.number] = surah.englishName; // Utiliser englishName pour l'affichage
          });
        } else {
          // Fallback: si getSurahList échoue ou n'est pas implémentée, charger manuellement (moins optimal)
          console.warn("[RangeSelector] getSurahList a échoué ou n'est pas implémentée, chargement manuel des noms de sourates.");
          for (let i = 1; i <= 114; i++) {
            try {
              const surah = await QuranAPI.getSurah(i);
              // L'API getSurah renvoie un objet avec une propriété 'name' (nom en arabe) et 'englishName'
              names[i] = surah.englishName;
            } catch (error) {
              console.error(`Erreur lors de la récupération de la sourate ${i}:`, error);
              names[i] = `Sourate ${i}`; // Fallback name
            }
          }
        }
        setAllSurahNames(names);
      } catch (error) {
        console.error('Erreur majeure lors de la récupération de la liste des sourates:', error);
        const fallbackNames = {};
        for (let i = 1; i <= 114; i++) {
          fallbackNames[i] = `Sourate ${i}`;
        }
        setAllSurahNames(fallbackNames);
      } finally {
        setLoadingSurahNames(false);
      }
    }
    loadAllSurahNames();
  }, []); // Se charge une seule fois au montage

  const maxValue = RANGE_LIMITS[rangeType] || RANGE_LIMITS.page;
  const labels = RANGE_LABELS[rangeType] || RANGE_LABELS.page;
  const usesNumericInput = rangeType !== 'surah';

  // Applique un raccourci de plage en restant dans les bornes autorisées
  const applyPreset = (preset) => {
    if (preset.start && preset.end) {
      onStartRangeChange(Math.min(preset.start, maxValue));
      onEndRangeChange(Math.min(preset.end, maxValue));
      return;
    }
    const start = Number(startRange) || 1;
    const end = Math.min(maxValue, start + preset.size - 1);
    onStartRangeChange(start);
    onEndRangeChange(end);
  };

  const handleStartRangeChange = (e) => {
    // Vérification de sécurité - n'accepter que des chaînes vides ou des nombres
    if (e.target.value === '') {
      onStartRangeChange('');
      return;
    }

    // Rejeter toute entrée non-numérique
    if (!/^\d+$/.test(e.target.value)) {
      return; // Ignorer les entrées non-numériques
    }

    const value = parseInt(e.target.value);
    // Vérifier si c'est un nombre valide
    if (!isNaN(value)) {
      // Si c'est dans la plage valide, mettre à jour
      if (value >= 1 && value <= maxValue) {
        onStartRangeChange(value);
        if (endRange !== '' && value > endRange) {
          onEndRangeChange(value);
        }
      }
    }
  };

  const handleEndRangeChange = (e) => {
    // Vérification de sécurité - n'accepter que des chaînes vides ou des nombres
    if (e.target.value === '') {
      onEndRangeChange('');
      return;
    }

    // Rejeter toute entrée non-numérique
    if (!/^\d+$/.test(e.target.value)) {
      return; // Ignorer les entrées non-numériques
    }

    const value = parseInt(e.target.value);
    // Vérifier si c'est un nombre valide
    if (!isNaN(value)) {
      // Si c'est dans la plage valide, mettre à jour
      if ((startRange === '' || value >= startRange) && value <= maxValue) {
        onEndRangeChange(value);
      }
    }
  };

  const renderSurahOptions = () => {
    if (loadingSurahNames && Object.keys(allSurahNames).length === 0) {
        return <option value="">Chargement des sourates...</option>;
    }
    return Array.from({ length: 114 }, (_, i) => i + 1).map(num => (
      <option key={num} value={num}>
        {num} - {allSurahNames[num] || `Sourate ${num}`}
      </option>
    ));
  };

  const rangeCount = Math.abs((Number(endRange) || 1) - (Number(startRange) || 1)) + 1;

  return (
    <div className="range-selector-container">
      <h2>{labels.title}</h2>

      <div className="range-presets">
        {(RANGE_PRESETS[rangeType] || []).map((preset) => (
          <button
            key={preset.label}
            type="button"
            className="range-preset-button"
            onClick={() => applyPreset(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="range-inputs-wrapper">
        {usesNumericInput ? (
          <>
            <div className="range-input-group">
              <label htmlFor="start-range">
                <PageIcon /> Début
              </label>
              <div className="input-with-icon">
                <input
                  id="start-range"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="1"
                  value={startRange}
                  onChange={handleStartRangeChange}
                  onBlur={() => {
                    // Si le champ est vide lors de la perte de focus, revenir à 1
                    if (startRange === '' || startRange < 1) {
                      onStartRangeChange(1);
                    }
                  }}
                />
              </div>
            </div>
            <div className="range-input-group">
              <label htmlFor="end-range">
                <PageIcon /> Fin
              </label>
              <div className="input-with-icon">
                <input
                  id="end-range"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={startRange || '1'}
                  value={endRange}
                  onChange={handleEndRangeChange}
                  onBlur={() => {
                    // Ramener la borne de fin dans les limites autorisées
                    if (endRange === '') {
                      onEndRangeChange(startRange || 1);
                    } else if (endRange > maxValue) {
                      onEndRangeChange(maxValue);
                    } else if (endRange < startRange) {
                      onEndRangeChange(startRange);
                    }
                  }}
                />
              </div>
            </div>
          </>
        ) : ( // rangeType === 'surah'
          <>
            <div className="range-input-group">
              <label htmlFor="start-surah">Début:</label>
              <select
                id="start-surah"
                value={startRange}
                onChange={handleStartRangeChange}
                disabled={loadingSurahNames && Object.keys(allSurahNames).length === 0}
              >
                {renderSurahOptions()}
              </select>
            </div>
            <div className="range-input-group">
              <label htmlFor="end-surah">Fin:</label>
              <select
                id="end-surah"
                value={endRange}
                onChange={handleEndRangeChange}
                disabled={loadingSurahNames && Object.keys(allSurahNames).length === 0}
              >
                {renderSurahOptions()}
              </select>
            </div>
          </>
        )}
      </div>

      <div className="range-info-display">
        {(loadingSurahNames && rangeType === 'surah' && Object.keys(allSurahNames).length === 0) ? (
          <p className="loading-text">
            <span className="loading-spinner"></span>
            Chargement des noms de sourates...
          </p>
        ) : (
          <p>
            <span className="info-icon">ⓘ</span>
            {usesNumericInput ? (
              <>
                Plage de {labels.plural}: <strong>{startRange || '1'}</strong> <ArrowIcon /> <strong>{endRange || startRange || '1'}</strong>{' '}
                <span className="total-info">({rangeCount} {rangeCount > 1 ? labels.plural : labels.singular} sur {maxValue})</span>
              </>
            ) : (
              <>
                Plage de sourates: <strong>{startRange}</strong>{allSurahNames[startRange] ? <span className="surah-name"> ({allSurahNames[startRange]})</span> : ''} <ArrowIcon /> <strong>{endRange}</strong>{allSurahNames[endRange] ? <span className="surah-name"> ({allSurahNames[endRange]})</span> : ''}{' '}
                <span className="total-info">({rangeCount} sur {maxValue})</span>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

export default RangeSelector;
